import React, { useState } from "react";
import {
  HiOutlineLocationMarker,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineClock,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import { Zap } from "lucide-react";

/* ─── constants ──────────────────────────────────────────────────────────── */
const WHATSAPP_NUMBER = "2349116459123";

/* ─── helpers ────────────────────────────────────────────────────────────── */
const buildMessage = (form) =>
  encodeURIComponent(
    [
      "Hello Electromob! I'm reaching out via your website contact form.",
      "",
      `*Full Name:* ${form.name}`,
      `*Email:* ${form.email}`,
      `*Phone:* ${form.phone || "Not provided"}`,
      `*Subject:* ${form.subject || "General enquiry"}`,
      "",
      `*Message:*`,
      form.message,
    ].join("\n")
  );

const validate = (form) => {
  const e = {};
  if (!form.name.trim())    e.name    = "Name is required";
  if (!form.email.trim())   e.email   = "Email is required";
  else if (!/\S+@\S+\.\S+/.test(form.email))
                            e.email   = "Enter a valid email";
  if (!form.message.trim()) e.message = "Please enter a message";
  return e;
};

const inputCls = (error) =>
  `w-full px-4 py-3 rounded-lg border text-sm bg-white
   focus:outline-none focus:ring-2 focus:ring-[#6ab615]/40
   focus:border-[#6ab615] transition-colors
   ${error ? "border-red-300 bg-red-50/30" : "border-gray-200"}`;

/* ─── subject options ────────────────────────────────────────────────────── */
const subjects = [
  { id: "purchase",      label: "Vehicle purchase",   icon: "🚗" },
  { id: "quote",         label: "Request a quote",    icon: "📋" },
  { id: "charging",      label: "Charging support",   icon: "🔌" },
  { id: "maintenance",   label: "Maintenance",        icon: "🔧" },
  { id: "fleet",         label: "Fleet enquiry",      icon: "🚌" },
  { id: "other",         label: "Other",              icon: "💬" },
];

/* ═══════════════════════════════════════════════════════════════════════════ */
const ContactP = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors]       = useState({});
  const [submitted, setSubmitted] = useState(false);

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleChange = (e) => set(e.target.name, e.target.value);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${buildMessage(form)}`,
      "_blank"
    );
    setSubmitted(true);
  };

  /* ── success screen ─────────────────────────────────────────────────── */
  if (submitted) {
    return (
      <div className="bg-white min-h-screen flex flex-col">
        {/* hero */}
        <div className="bg-gradient-to-br from-[#102a05] to-[#4a8f0f] text-white
                        pt-36 pb-20 px-6 md:px-10 lg:px-16">
          <span className="text-white/70 font-semibold uppercase tracking-widest text-xs">
            Contact
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mt-3">
            Message sent!
          </h1>
        </div>

        {/* body */}
        <div className="flex-1 flex flex-col items-center justify-center
                        text-center px-6 py-24 gap-5">
          <div className="bg-[#6ab615]/10 text-[#4a8f0f] rounded-full p-6">
            <HiOutlineCheckCircle className="text-5xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            Thanks for reaching out
          </h2>
          <p className="text-gray-500 max-w-md leading-relaxed text-sm">
            WhatsApp has opened with your message pre-filled. Our team
            will get back to you within 1–2 business days. Feel free to
            send a follow-up message directly if you need a faster reply.
          </p>

          <div className="flex flex-wrap gap-3 justify-center mt-2">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 bg-[#6ab615] hover:bg-[#5a9b12]
                         text-white font-semibold px-5 py-3 rounded-xl
                         transition-colors duration-200 text-sm"
            >
              <Zap className="w-4 h-4" />
              Open WhatsApp
            </a>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm({
                  name: "", email: "", phone: "",
                  subject: "", message: "",
                });
              }}
              className="text-sm font-semibold text-gray-500 hover:text-gray-800
                         border border-gray-200 px-5 py-3 rounded-xl
                         transition-colors duration-200"
            >
              Send another message
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── main page ──────────────────────────────────────────────────────── */
  return (
    <div className="bg-white min-h-screen">

      {/* ── hero band ─────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#102a05] to-[#4a8f0f] text-white
                      pt-36 pb-20 px-6 md:px-10 lg:px-16">
        <span className="text-white/70 font-semibold uppercase tracking-widest text-xs">
          Contact
        </span>
        <h1 className="text-4xl md:text-5xl font-bold mt-3">
          Get in touch
        </h1>
        <p className="text-white/80 mt-4 max-w-xl text-sm leading-relaxed">
          Questions about an electric vehicle, a quote, or a partnership?
          We'd love to hear from you.
        </p>

        {/* quick-action pills */}
        <div className="flex flex-wrap gap-3 mt-8">
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 bg-white/15 hover:bg-white/25
                       backdrop-blur-sm text-white text-xs font-medium
                       px-4 py-2 rounded-full transition-colors duration-200"
          >
            <Zap className="w-3.5 h-3.5" />
            WhatsApp us
          </a>
          <a
            href="tel:+2348175262273"
            className="flex items-center gap-2 bg-white/15 hover:bg-white/25
                       backdrop-blur-sm text-white text-xs font-medium
                       px-4 py-2 rounded-full transition-colors duration-200"
          >
            <HiOutlinePhone className="text-sm" />
            Call us
          </a>
          <a
            href="mailto:info@electromob.com.ng"
            className="flex items-center gap-2 bg-white/15 hover:bg-white/25
                       backdrop-blur-sm text-white text-xs font-medium
                       px-4 py-2 rounded-full transition-colors duration-200"
          >
            <HiOutlineMail className="text-sm" />
            Email us
          </a>
        </div>
      </div>

      {/* ── info + form grid ──────────────────────────────────────────── */}
      <div className="px-6 md:px-10 lg:px-16 py-16 grid gap-12
                      lg:grid-cols-5 max-w-7xl mx-auto">

        {/* contact info */}
        <aside className="lg:col-span-2 flex flex-col gap-6">
          <InfoCard
            icon={<HiOutlineLocationMarker className="text-xl" />}
            title="Visit us"
            lines={["D Close 2 Flat 6, Games Village", "Abuja, Nigeria"]}
          />
          <InfoCard
            icon={<HiOutlinePhone className="text-xl" />}
            title="Call us"
            lines={["+234 817 526 2273"]}
            link="tel:+2348175262273"
          />
          <InfoCard
            icon={<HiOutlineMail className="text-xl" />}
            title="Email us"
            lines={[
              "info@electromob.com.ng",
              "erickanjal@electromob.com.ng",
            ]}
            link="mailto:info@electromob.com.ng"
          />
          <InfoCard
            icon={<HiOutlineClock className="text-xl" />}
            title="Working hours"
            lines={["Mon – Fri: 9am – 6pm", "Sat: 10am – 2pm"]}
          />

          {/* WhatsApp shortcut */}
          <div className="bg-[#f4faec] rounded-2xl p-6 border border-[#6ab615]/20 mt-2">
            <p className="text-xs uppercase tracking-widest text-[#4a8f0f]
                          font-semibold mb-2">
              Fastest response
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              For the quickest reply, reach us directly on WhatsApp.
              We typically respond within a few hours during business hours.
            </p>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-[#6ab615]
                         hover:bg-[#5a9b12] text-white text-sm font-semibold
                         px-4 py-2.5 rounded-xl transition-colors duration-200"
            >
              <Zap className="w-4 h-4" />
              Chat on WhatsApp
            </a>
          </div>
        </aside>

        {/* form */}
        <div className="lg:col-span-3 bg-gray-50 rounded-2xl p-8 md:p-10
                        border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            Send us a message
          </h2>
          <p className="text-sm text-gray-500 mb-7">
            Fill in the form below and we'll get back to you promptly.
            Your message will be sent directly to our WhatsApp.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>

            {/* name + email */}
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full name" error={errors.name} required>
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={form.name}
                  onChange={handleChange}
                  className={inputCls(errors.name)}
                />
              </Field>
              <Field label="Email address" error={errors.email} required>
                <input
                  type="email"
                  name="email"
                  placeholder="name@email.com"
                  value={form.email}
                  onChange={handleChange}
                  className={inputCls(errors.email)}
                />
              </Field>
            </div>

            {/* phone */}
            <Field label="Phone number (optional)">
              <input
                type="tel"
                name="phone"
                placeholder="+234 800 000 0000"
                value={form.phone}
                onChange={handleChange}
                className={inputCls()}
              />
            </Field>

            {/* subject picker */}
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-gray-700">
                What's this about?{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {subjects.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() =>
                      set("subject", form.subject === s.label ? "" : s.label)
                    }
                    className={`flex items-center gap-2 px-3 py-3 rounded-xl
                                border text-xs font-medium transition-all duration-200
                                ${
                                  form.subject === s.label
                                    ? "bg-[#6ab615]/10 border-[#6ab615] text-[#4a8f0f]"
                                    : "bg-white border-gray-200 text-gray-600 hover:border-[#6ab615]/50"
                                }`}
                  >
                    <span className="text-base">{s.icon}</span>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* message */}
            <Field label="Message" error={errors.message} required>
              <textarea
                name="message"
                rows={6}
                placeholder="Tell us how we can help..."
                value={form.message}
                onChange={handleChange}
                className={`${inputCls(errors.message)} resize-none`}
              />
            </Field>

            {/* submit */}
            <div className="flex flex-col sm:flex-row items-start
                            sm:items-center gap-4 pt-1">
              <button
                type="submit"
                className="flex items-center justify-center gap-2
                           bg-[#6ab615] hover:bg-[#5a9b12] text-white
                           font-semibold px-8 py-4 rounded-xl
                           transition-colors duration-200 text-sm
                           w-full sm:w-fit"
              >
                <Zap className="w-4 h-4" />
                Send via WhatsApp
              </button>
              <p className="text-xs text-gray-400 leading-relaxed max-w-xs">
                Tapping send will open WhatsApp with your message
                pre-filled. No data is stored on this site.
              </p>
            </div>

          </form>
        </div>
      </div>

      {/* ── map ───────────────────────────────────────────────────────── */}
      <div className="px-6 md:px-10 lg:px-16 pb-16 max-w-7xl mx-auto">
        <div className="rounded-2xl overflow-hidden border border-gray-100
                        h-[320px] bg-gray-100 shadow-sm">
          <iframe
            title="Electromob location"
            src="https://www.google.com/maps?q=Games+Village,Abuja,Nigeria&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
};

/* ─── sub-components ─────────────────────────────────────────────────────── */
const InfoCard = ({ icon, title, lines, link }) => (
  <div className="flex gap-4 items-start">
    <div className="bg-[#6ab615]/10 text-[#4a8f0f] p-3 rounded-xl shrink-0">
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
      {lines.map((line) =>
        link ? (
          <a
            key={line}
            href={link}
            className="text-sm text-gray-500 mt-0.5 block hover:text-[#4a8f0f]
                       transition-colors duration-200"
          >
            {line}
          </a>
        ) : (
          <p key={line} className="text-sm text-gray-500 mt-0.5">
            {line}
          </p>
        )
      )}
    </div>
  </div>
);

const Field = ({ label, children, error, required }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
    {error && (
      <p className="text-xs text-red-500 mt-0.5">{error}</p>
    )}
  </div>
);

export default ContactP;